import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PillChip } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import {
  getConversation,
  sendMessage,
  getSuggestions,
  markNotificationsRead,
  parseInquiryError,
  REASON_CODE_LABELS,
} from "@/lib/modulD";
import { ArrowLeft, Send, AlertCircle, Lock, Sparkles, Info, Flag } from "lucide-react";
import MessageReportDialog from "@/components/MessageReportDialog";
import { InfoCard } from "@/components/toggles";
import RichTextEditor from "@/components/common/RichTextEditor";

const STATUS_COLOR = {
  OPEN: "text-green-600 border-green-200 bg-green-50",
  CLOSED: "text-[#999] border-[#E5E5E5] bg-[#FAFAFA]",
  ESCALATED: "text-amber-600 border-amber-200 bg-amber-50",
};

export default function InquiryThreadPage() {
  const { t } = useTranslation();
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [block, setBlock] = useState(null); // { reason_code, reason_text }
  const [warning, setWarning] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  // Sub-Batch 4c.5: Report dialog state
  const [reportingMessage, setReportingMessage] = useState(null); // {message_id, content_display}
  const [reportedIds, setReportedIds] = useState(new Set());
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestCategory, setSuggestCategory] = useState(null);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await getConversation(conversationId);
      setData(d);
      setError(null);
      setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 100);
    } catch (e) {
      let msg = t("inquiry_thread.load_error");
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string") msg = detail;
      else if (detail && typeof detail === "object") msg = detail.code || detail.message || JSON.stringify(detail);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    // Warten bis Auth-Status klar ist
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    load();
    // Mark notifications for this conversation as read
    markNotificationsRead({ conversation_id: conversationId }).catch(() => {});
  }, [user, authLoading, load, navigate, conversationId]);

  // Lightweight polling for new messages (every 8s while page open and OPEN)
  useEffect(() => {
    if (!data || data.conversation?.status !== "OPEN") return;
    const id = setInterval(() => { load(); }, 8000);
    return () => clearInterval(id);
  }, [data, load]);

  const handleSend = async () => {
    // HTML aus RichTextEditor auf reinen Text reduzieren
    const plainText = (text || "")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    if (!plainText || sending) return;
    setSending(true);
    setBlock(null);
    setWarning(null);
    try {
      const result = await sendMessage(conversationId, plainText);
      setText("");
      // Show sender warning if redaction occurred
      const mod = result?.moderation;
      if (mod && (mod.decision === "REDACTED" || mod.redaction_count > 0)) {
        const c = mod.redaction_count;
        const kind = c === 1 ? t("inquiry_thread.redact_kind_singular") : t("inquiry_thread.redact_kind_plural");
        setWarning(t("inquiry_thread.redact_notice", { count: c || "", kind }));
      }
      await load();
    } catch (e) {
      const parsed = parseInquiryError(e);
      if (parsed.blocked) {
        setBlock(parsed);
      } else if (parsed.feature_disabled) {
        setError(t("inquiry_thread.feature_disabled"));
      } else {
        setError(parsed.message);
      }
    } finally {
      setSending(false);
    }
  };

  const loadSuggestions = async (cat) => {
    setSuggestCategory(cat);
    setSuggestLoading(true);
    setSuggestions([]);
    try {
      const res = await getSuggestions(conversationId, cat);
      setSuggestions(res.suggestions || []);
    } catch (e) {
      setSuggestions([]);
    } finally {
      setSuggestLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;
  }
  if (error || !data) {
    return (
      <div className="px-4 py-16 text-center">
        <p className="text-sm text-[#999]">{typeof error === "string" ? error : t("inquiry_thread.conv_not_found")}</p>
        <button onClick={() => navigate("/messages")} className="mt-4 text-xs text-[#0A0A0A] underline">{t("inquiry_thread.back")}</button>
      </div>
    );
  }

  const conv = data.conversation;
  const messages = data.messages || [];
  const chatActive = data.chat_active;
  const viewerRole = data.viewer_role;
  const otherDisplay = data.other_display_name;
  const closedReason = conv.closed_reason;
  const isSeller = viewerRole === "SELLER";
  const isMarketplace = conv.shop_area === "MARKETPLACE";
  const sellerCategories = isMarketplace
    ? [{ key: "sachlich", label: t("inquiry_thread.cat_sachlich") }, { key: "zustand", label: t("inquiry_thread.cat_zustand") }, { key: "versand", label: t("inquiry_thread.cat_versand") }, { key: "klarend", label: t("inquiry_thread.cat_klarend") }]
    : [{ key: "freundlich", label: t("inquiry_thread.cat_freundlich") }, { key: "charmant", label: t("inquiry_thread.cat_charmant") }, { key: "erotisch", label: t("inquiry_thread.cat_erotisch_subtil") }, { key: "verkaufsfordernd", label: t("inquiry_thread.cat_verkaufsfordernd") }];

  return (
    <div data-testid="inquiry-thread-page" className="flex flex-col bg-white" style={{height: "calc(100vh - 144px)"}}>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-[#E5E5E5] px-4 py-3 flex items-center gap-3">
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="p-1 -ml-1"><ArrowLeft size={20} /></button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[#0A0A0A] truncate">{otherDisplay}</p>
          <p className="text-[10px] text-[#999] truncate">
            {conv.shop_area === "MARKETPLACE" ? t("inquiry_thread.marketplace_inquiry") : t("inquiry_thread.after_sale_chat")}
            {data.seconds_remaining !== null && data.seconds_remaining !== undefined && (
              <> {t("inquiry_thread.remaining_prefix", { value: formatRemaining(data.seconds_remaining, t) })}</>
            )}
          </p>
        </div>
        <span className={`text-[10px] px-2 py-0.5 border rounded-full ${STATUS_COLOR[conv.status]}`}>{
          conv.status === "OPEN" ? t("inquiry_thread.status_open") :
          conv.status === "CLOSED" ? t("inquiry_thread.status_closed") :
          conv.status === "ESCALATED" ? t("inquiry_thread.status_escalated") :
          conv.status
        }</span>
      </div>

      {/* Listing card (sticky below header) */}
      {data.listing && conv.listing_id && (
        <button data-testid="thread-listing-card"
          onClick={() => navigate(conv.shop_area === "MARKETPLACE" ? `/marketplace/${conv.listing_id}` : `/stilltouchedx/${conv.listing_id}`)}
          className="w-full flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5] bg-[#FAFAFA] hover:bg-[#F5F5F5] transition-colors text-left">
          <div className="w-12 h-14 bg-[#E5E5E5] shrink-0 overflow-hidden">
            {data.listing.media_urls?.[0] && <img src={data.listing.media_urls[0]} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[#999]">{t("inquiry_thread.article")}</p>
            <p className="text-sm font-medium text-[#0A0A0A] truncate">{data.listing.title || t("inquiry_thread.article_fallback")}</p>
            {data.listing.price !== undefined && <p className="text-xs text-[#666] mt-0.5">{Number(data.listing.price).toFixed(2)} EUR</p>}
          </div>
          <span className="text-[10px] text-[#999] shrink-0">›</span>
        </button>
      )}

      {/* Closed banner */}
      {conv.status === "CLOSED" && (
        <div className="mx-4 mt-3 px-3 py-2 bg-[#FAFAFA] border border-[#E5E5E5] flex items-start gap-2">
          <Lock size={14} className="text-[#999] mt-0.5 shrink-0" />
          <p className="text-xs text-[#666]">{
            (closedReason === "TIME_EXPIRED" && t("inquiry_thread.closed_time_expired")) ||
            (closedReason === "ITEM_UNAVAILABLE" && t("inquiry_thread.closed_item_unavailable")) ||
            (closedReason === "MANUAL" && t("inquiry_thread.closed_manual")) ||
            t("inquiry_thread.closed_default")
          } {t("inquiry_thread.closed_hint")}</p>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <p className="text-center text-xs text-[#CCC] py-8">{t("inquiry_thread.no_messages")}</p>
        )}
        {messages.map((m) => (
          <MessageItem
            key={m.message_id}
            message={m}
            viewerRole={viewerRole}
            onReport={() => setReportingMessage(m)}
            reportedIds={reportedIds}
          />
        ))}
      </div>

      {/* Composer or closed/blocked feedback */}
      {chatActive && conv.status === "OPEN" && (
        <div className="border-t border-[#E5E5E5] bg-white">
          {/* Block dialog */}
          {block && (
            <InfoCard variant="danger" testId="block-banner" className="mx-4 mt-3">
              <AlertCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-xs font-bold text-red-700">{REASON_CODE_LABELS[block.reason_code] || t("inquiry_thread.message_blocked_title")}</p>
                <p className="text-xs text-red-700 mt-0.5">{block.reason_text}</p>
                <button onClick={() => setBlock(null)} className="text-[10px] text-red-700 underline mt-1">{t("inquiry_thread.understood")}</button>
              </div>
            </InfoCard>
          )}
          {warning && (
            <InfoCard variant="warning" testId="redact-warning" className="mx-4 mt-3">
              <AlertCircle size={16} className="text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-[11px] text-amber-900 leading-relaxed">{warning}</p>
                <button onClick={() => setWarning(null)} className="text-[10px] text-amber-700 underline mt-1 hover:text-amber-900">{t("inquiry_thread.understood")}</button>
              </div>
            </InfoCard>
          )}
          {/* KI Suggestions (seller-only) */}
          {isSeller && (
            <div className="px-4 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={12} className="text-[#0A0A0A]" />
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#999]">{t("inquiry_thread.reply_suggestions")}</p>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                {sellerCategories.map(cat => (
                  <PillChip key={cat.key}
                    testId={`suggest-cat-${cat.key}`}
                    active={suggestCategory === cat.key}
                    onClick={() => loadSuggestions(cat.key)}
                    variant="neutral"
                    size="sm">
                    {cat.label}
                  </PillChip>
                ))}
              </div>
              {suggestLoading && <p className="text-[10px] text-[#999] py-1">…</p>}
              {suggestions.length > 0 && (
                <div className="space-y-1 mb-2">
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => { setText(s.text); setSuggestions([]); setSuggestCategory(null); }}
                      className="w-full text-left text-xs px-2 py-1.5 bg-[#FAFAFA] border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors"
                      data-testid={`suggest-${i}`}>{s.text}</button>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Composer */}
          <div className="px-4 py-3 space-y-2">
            <div data-testid="compose-text">
              <RichTextEditor
                value={text}
                onChange={(html) => setText(html)}
                placeholder={isMarketplace
                  ? t("inquiry_thread.placeholder_marketplace")
                  : t("inquiry_thread.placeholder_default")}
                minHeight="90px"
                testId="compose-text-rt"
              />
            </div>
            <div className="flex justify-end">
              <button
                data-testid="compose-send"
                onClick={handleSend}
                disabled={sending || !(text || "").replace(/<[^>]+>/g, "").trim()}
                /* ui-inventory-ignore: composer send button — Tiptap-editor sibling */
                className="rounded-full px-4 py-2 bg-[#0A0A0A] text-white text-xs font-bold disabled:opacity-30 flex items-center gap-1">
                <Send size={14} />{sending ? "…" : t("inquiry_thread.send")}
              </button>
            </div>
          </div>
          {isMarketplace && (
            <div className="px-4 pb-3 flex items-start gap-1.5">
              <Info size={10} className="text-[#999] mt-0.5 shrink-0" />
              <p className="text-[10px] text-[#999]">{t("inquiry_thread.no_contact_data")}</p>
            </div>
          )}
        </div>
      )}

      {/* Message-Report-Dialog (Sub-Batch 4c.5) */}
      <MessageReportDialog
        open={!!reportingMessage}
        onClose={() => setReportingMessage(null)}
        messageKind="CONV"
        messageId={reportingMessage?.message_id}
        messagePreview={reportingMessage?.content_display}
        onReported={() => {
          if (reportingMessage?.message_id) {
            setReportedIds((prev) => new Set([...prev, reportingMessage.message_id]));
          }
        }}
      />
    </div>
  );
}

function MessageItem({ message, viewerRole, onReport, reportedIds }) {
  const { t } = useTranslation();
  const mine = message.sender_role === viewerRole;
  const isSystem = message.sender_role === "SYSTEM" || message.is_system;
  const isBlocked = message.moderation_status === "BLOCKED";
  const isRedacted = message.moderation_status === "REDACTED";
  const canReport = !mine && !isSystem && !isBlocked && !!onReport;
  const alreadyReported = reportedIds?.has?.(message.message_id);

  if (isSystem) {
    return (
      <div data-testid={`msg-${message.message_id}`} className="text-center">
        <div className="inline-block max-w-[80%] px-3 py-1.5 text-[11px] text-[#999] bg-[#FAFAFA] border border-[#E5E5E5]">
          {message.content_display}
        </div>
      </div>
    );
  }

  if (isBlocked) {
    const reasonCode = message.moderation_primary_reason || (message.moderation_reason_codes && message.moderation_reason_codes[0]) || "BLOCKED";
    const reasonLabel = REASON_CODE_LABELS[reasonCode] || t("inquiry_thread.message_blocked_title");
    return (
      <div data-testid={`msg-${message.message_id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
        <div className={`max-w-[80%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
          {!mine && <span className="text-[10px] text-[#999] mb-0.5">{message.sender_display_name}</span>}
          <InfoCard variant="danger" className="max-w-full">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-red-700">{t("inquiry_thread.not_delivered")}</span>
            </div>
            <p className="text-xs text-red-700 italic line-through">{message.content_display}</p>
            <p className="text-[10px] text-red-600 mt-1.5">{t("inquiry_thread.msg_not_delivered_reason", { reason: reasonLabel })}</p>
          </InfoCard>
          <span className="text-[10px] text-[#CCC] mt-0.5">{timeAgo(message.created_at, t)}</span>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`msg-${message.message_id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
        {!mine && (
          <span className="text-[10px] text-[#999] mb-0.5">{message.sender_display_name}</span>
        )}
        <div className={`px-3 py-2 text-sm ${
          mine ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] text-[#0A0A0A]"
        }`}>
          {message.content_display}
          {isRedacted && (
            <div className={`text-[10px] mt-1 italic ${mine ? "text-white/60" : "text-[#999]"}`}>
              {t("inquiry_thread.redacted_hint", { count: message.moderation_redaction_count })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-[#CCC]">{timeAgo(message.created_at, t)}</span>
          {canReport && (
            <button
              data-testid={`report-btn-${message.message_id}`}
              onClick={onReport}
              disabled={alreadyReported}
              className={`text-[10px] flex items-center gap-0.5 ${
                alreadyReported
                  ? "text-[#CCC] cursor-default"
                  : "text-[#999] hover:text-red-600"
              }`}
              title={alreadyReported ? t("inquiry_thread.already_reported_tooltip") : t("inquiry_thread.report_this_msg")}
            >
              <Flag size={9} />
              {alreadyReported ? t("inquiry_thread.reported") : t("inquiry_thread.report")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function timeAgo(dateStr, t) {
  if (!dateStr) return "";
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return t("inquiry_thread.time_now");
  if (diff < 3600) return t("inquiry_thread.time_min_ago", { n: Math.floor(diff / 60) });
  if (diff < 86400) return t("inquiry_thread.time_hour_ago", { n: Math.floor(diff / 3600) });
  return t("inquiry_thread.time_day_ago", { n: Math.floor(diff / 86400) });
}

function formatRemaining(secs, t) {
  if (secs <= 0) return t("inquiry_thread.remaining_expired");
  const days = Math.floor(secs / 86400);
  if (days >= 1) return t("inquiry_thread.remaining_days", { count: days });
  const hours = Math.floor(secs / 3600);
  if (hours >= 1) return t("inquiry_thread.remaining_hours", { count: hours });
  return t("inquiry_thread.remaining_minutes", { count: Math.max(1, Math.floor(secs / 60)) });
}
